import ContainerSections from '../../components/container-sections';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { resumeRealtimePlayback } from '../../services/realtime-connection-store';
import { changeAppLanguage } from '../../../i18n';
import CameraSide from '../../components/camera-permission/camera-side/camera-side';
import ContentSide from '../../components/camera-permission/content-side/content-side';
import tw from 'twin.macro';
import { css } from 'styled-components';
import { useDevice } from '../../zustand/devices-store';
import { useSessionMetadataStore } from '../../zustand/session-metadata-store';
import { recordExerciseVideo, setRecordExerciseVideo } from '../../utilis/exercise-video';
import useRealtimeVoice from '../../hooks/use-realtime-voice';
import { prefetchSessionMedia } from '../../utilis/session-media-cache';
import { ensureSessionExitUrlsFetched } from '../../utilis/session-exit-urls';

// dvh tracks the viewport actually visible on iOS Safari; 100vh is the static
// viewport, which is taller than the screen and pushed the Continue button
// under the browser toolbar. The vh line is the fallback for engines without
// dvh and must stay a separate declaration — twin would collapse a `tw` class
// pair into one height key.
const pageHeight = css`
  height: 100vh;
  height: 100dvh;
`;

const CameraPermissions = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('sid') || searchParams.get('sessionId');
  const localeParam = searchParams.get('locale');
  const voiceCompanionMode = searchParams.get('voiceCompanionMode');
  // Forwarded verbatim to ContentSide, which relays it onto /try-demo → /exercise —
  // it authenticates every inPerformance API call along the way (see
  // api-instance.js and apis/realtime/api.js, both read it straight off the URL).
  const sessionToken = searchParams.get('session_token');
  // Same relay, optional — lands in the recorded video's filename on /exercise
  // (see use-recording-video.jsx's buildFileName), omitted there when absent.
  const patientName = searchParams.get('patientName');

  // Only reads the param — sid/sessionId parsing above is untouched and the
  // URL itself is never rewritten. changeAppLanguage no-ops on an unsupported
  // or already-active code, and persists to localStorage for later pages.
  useEffect(() => {
    if (localeParam) {
      changeAppLanguage(localeParam);
    }
  }, [localeParam]);
  const { audioDevice, changeAudioDevice } = useDevice();
  // Seeded by CameraManager itself once permission is granted and the device
  // list is enumerated (see camera-side.jsx) — no longer read from
  // localStorage here, since CameraManager owns that persistence directly.
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [permissionsGranted, setPermissionsGranted] = useState(true);
  const [permissionChecked, setPermissionChecked] = useState(false);
  const [noCameraAvailable, setNoCameraIsavalebale] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [cameraInUse, setIsCameraInUse] = useState(false);
  const [selectedMicrophone, setSelectedMicrophone] = useState(audioDevice);
  const videoRecordingAvailable = useSessionMetadataStore(
    state => state.sessionId === sessionId && state.videoRecordingAvailable
  );
  const [recordVideo, setRecordVideo] = useState(() => recordExerciseVideo(sessionId));
  const changeRecordVideo = on => {
    setRecordVideo(on);
    setRecordExerciseVideo(sessionId, on);
  };
  const navigate = useNavigate();

  // Hoisted out of ContentSide because two places offer this exit now: its own
  // CTA in the normal flow, and the camera-unavailable card, which is the whole
  // camera column while it is up. Every param it forwards is already read here.
  const handleContinueToDemo = () => {
    // The only guaranteed user gesture before the exercise starts. The voice
    // call was opened on page mount, which on a directly-linked visit happens
    // with no interaction in the document at all — so the autoplay policy
    // refused its play() and the agent is connected but silent. Retrying from
    // inside this click is what beats the policy. Fire-and-forget: navigation
    // must not wait on it, and a second refusal is already logged by the store.
    resumeRealtimePlayback('camera-permission:continue-click');
    const tryDemoParams = new URLSearchParams({ sid: sessionId });
    if (voiceCompanionMode) {
      tryDemoParams.set('voiceCompanionMode', voiceCompanionMode);
    }
    if (sessionToken) {
      tryDemoParams.set('session_token', sessionToken);
    }
    if (patientName) {
      tryDemoParams.set('patientName', patientName);
    }
    navigate(`/try-demo?${tryDemoParams.toString()}`, { replace: true });
    document.body.requestFullscreen();
  };

  // The voice call is opened here, not on /exercise.
  //
  // Three requests have to complete before the coach can say anything —
  // POST /realtime/token/, POST api.openai.com/v1/realtime/calls, then
  // POST /realtime/register-call/. Starting them on /exercise left them racing
  // the intro dwell: the backend fires `welcome` on the config frame, and any
  // event raised before the call id is registered is built, logged, and never
  // spoken. Opening the call here gives the handshake the whole camera-check
  // and instruction-slide dwell to finish in, and surfaces a failure while the
  // patient is still on a page where it is harmless.
  //
  // The same target (`sessionId::exerciseId`) is passed on /exercise, where the
  // store recognises it and reuses this call — no second token, no second call.
  // Both pages read `exerciseId` from the same localStorage key, written before
  // the navigation here (prescriptionBuilder) and rewritten to the same value
  // by /try-demo.
  useRealtimeVoice(sessionId, localStorage.getItem('exerciseId') || null);

  // Earliest point in the guest flow with a session id, so this is where the
  // one-time metadata fetch actually starts — /try-demo and /exercise call the
  // same fetchMetadata, but the store dedupes by session id and both of those
  // requests will already be sitting on this page's result by the time they run.
  useEffect(() => {
    useSessionMetadataStore.getState().fetchMetadata(sessionId);
  }, [sessionId]);

  // Same reasoning as the metadata fetch above: earliest point in the guest
  // flow with a session id, so completeUrl/exitUrl (see
  // utilis/session-exit-urls.js) have the whole camera-check dwell to land in
  // initialConfig before /try-demo's own call to this same dedupe reuses it,
  // and long before /exercise reads initialConfig at mount.
  useEffect(() => {
    ensureSessionExitUrlsFetched(sessionId);
  }, [sessionId]);

  useEffect(() => {
    if (selectedMicrophone) {
      changeAudioDevice(selectedMicrophone);
    }
  }, [selectedMicrophone]);

  // Camera in use: the two-column split is about a camera preview and the copy
  // beside it, and neither exists in this state — the alert is the only thing
  // left on the page, so it gets the whole viewport and centres in it instead
  // of sitting in the left column with an empty right half. The page's own
  // asymmetric side margins are dropped too, since they would push a centred
  // card off the middle of the screen.
  const isCameraAlertOnly = cameraInUse;

  return (
    <ContainerSections
      otherStyles={[
        isCameraAlertOnly
          ? tw`rounded-none grid place-items-center mx-0! xl:mx-0!`
          : tw`rounded-none grid items-center justify-center`,
        pageHeight,
      ]}
    >
      {/* Portrait mobile scrolls rather than clipping: on a short phone the
          camera preview + device pickers + copy can still outrun the viewport,
          and overflow-hidden made the Continue button unreachable.

          Explicit 70/30 rows there: the camera (preview + device pickers) owns
          the top 70% of the viewport and the copy/CTA the remaining 30%. Fixed
          tracks rather than auto ones, so the rows can't be stretched apart
          into a dead band or squeezed by a long device name. */}
      <div
        css={
          isCameraAlertOnly
            ? tw`grid place-items-center p-5 w-full h-full min-h-0`
            : tw`grid grid-cols-1 max-lg:[@media(orientation:landscape)]:grid-cols-[1.3fr_1fr] lg:grid-cols-[1.3fr_1fr] w-full justify-center h-full min-h-0 overflow-y-auto max-lg:[@media(orientation:portrait)]:grid-rows-[70%_30%] max-lg:[@media(orientation:portrait)]:overflow-hidden max-lg:[@media(orientation:landscape)]:h-full lg:h-[90vh] lg:overflow-hidden md:p-5`
        }
      >
        <CameraSide
          cameraInUse={cameraInUse}
          onContinueWithoutCamera={handleContinueToDemo}
          isActive={isActive}
          noCameraAvailable={noCameraAvailable}
          permissionChecked={permissionChecked}
          permissionsGranted={permissionsGranted}
          selectedCamera={selectedCamera}
          setIsActive={setIsActive}
          setIsCameraInUse={setIsCameraInUse}
          setNoCameraIsavalebale={setNoCameraIsavalebale}
          setPermissionChecked={setPermissionChecked}
          setPermissionsGranted={setPermissionsGranted}
          setSelectedCamera={setSelectedCamera}
        />
        {!isCameraAlertOnly && (
          <ContentSide
            cameraInUse={cameraInUse}
            onContinue={handleContinueToDemo}
            isActive={isActive}
            noCameraAvailable={noCameraAvailable}
            permissionChecked={permissionChecked}
            permissionsGranted={permissionsGranted}
            selectedMicrophone={selectedMicrophone}
            videoToggle={
              videoRecordingAvailable ? { checked: recordVideo, onChange: changeRecordVideo } : null
            }
          />
        )}
      </div>
    </ContainerSections>
  );
};

export default CameraPermissions;
